// Tiny cart helper. The cart is just { [rate_id]: qty } kept in sessionStorage
// so it survives navigation between the shop and checkout. Prices/VAT are never
// stored here — the server is the single source of truth for money; the cart
// only carries quantities.
//
// `scope` (optional) keys a separate cart per context — the public storefront
// passes the event id (key `shopCart:<event_id>`) so two events never share a
// basket; the contact-person shop keeps calling without it, unchanged.
const KEY = 'shopCart';
const keyFor = (scope) => (scope ? `${KEY}:${scope}` : KEY);

export const readCart = (scope) => {
  try {
    return JSON.parse(sessionStorage.getItem(keyFor(scope))) || {};
  } catch {
    return {};
  }
};

export const writeCart = (cart, scope) => {
  sessionStorage.setItem(keyFor(scope), JSON.stringify(cart || {}));
};

export const clearCart = (scope) => sessionStorage.removeItem(keyFor(scope));

export const cartCount = (cart) =>
  Object.values(cart || {}).reduce((n, q) => n + (Number(q) || 0), 0);

// Compute display totals from the live products + the cart quantities. Mirrors
// the server's arithmetic (net = price*qty, vat = net*rate/100) so the UI and
// the invoice agree, but it is display-only.
export const computeTotals = (products, cart, withVat) => {
  let subtotal = 0;
  let vat = 0;
  (products || []).forEach((p) => {
    const qty = Number(cart?.[p.rate_id]) || 0;
    if (qty <= 0) return;
    // Charge the discounted price when one is active; fall back to list price.
    const unit = p.effective_price ?? p.unit_price;
    const net = unit * qty;
    subtotal += net;
    if (withVat) vat += net * (Number(p.vat_rate) || 0) / 100;
  });
  return { subtotal, vat, total: subtotal + vat };
};
